import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Stack,
  InputAdornment,
  Divider
} from '@mui/material';

interface CostBreakdownProps {
  baseCost: number;
  distributorMarginPct?: number;
  importTaxPct?: number;
  onChange?: (values: {
    baseCost: number;
    distributorMarginPct: number;
    importTaxPct: number;
    totalCost: number;
  }) => void;
  label?: string;
}

export default function CostBreakdownCalculator({
  baseCost,
  distributorMarginPct = 3.00,
  importTaxPct = 10.00,
  onChange,
  label = "Cost Breakdown"
}: CostBreakdownProps) {
  // Calculate breakdown - ensure all values are valid numbers
  const safeBaseCost = Number(baseCost) || 0;
  const safeMarginPct = Number(distributorMarginPct) || 0;
  const safeImportPct = Number(importTaxPct) || 0;

  const distributorMargin = safeBaseCost * (safeMarginPct / 100);
  const costAfterMargin = safeBaseCost + distributorMargin;
  const importTax = costAfterMargin * (safeImportPct / 100);
  const totalCost = costAfterMargin + importTax;

  const handleBaseCostChange = (value: string) => {
    const newBaseCost = parseFloat(value) || 0;
    if (onChange) {
      const newDistMargin = newBaseCost * (distributorMarginPct / 100);
      const newCostAfterMargin = newBaseCost + newDistMargin;
      const newImportTax = newCostAfterMargin * (importTaxPct / 100);
      const newTotalCost = newCostAfterMargin + newImportTax;

      onChange({
        baseCost: newBaseCost,
        distributorMarginPct,
        importTaxPct,
        totalCost: newTotalCost
      });
    }
  };

  const handleMarginChange = (value: string) => {
    const newMarginPct = parseFloat(value) || 0;
    if (onChange) {
      const newDistMargin = baseCost * (newMarginPct / 100);
      const newCostAfterMargin = baseCost + newDistMargin;
      const newImportTax = newCostAfterMargin * (importTaxPct / 100);
      const newTotalCost = newCostAfterMargin + newImportTax;

      onChange({
        baseCost,
        distributorMarginPct: newMarginPct,
        importTaxPct,
        totalCost: newTotalCost
      });
    }
  };

  const handleImportChange = (value: string) => {
    const newImportPct = parseFloat(value) || 0;
    if (onChange) {
      const newDistMargin = baseCost * (distributorMarginPct / 100);
      const newCostAfterMargin = baseCost + newDistMargin;
      const newImportTax = newCostAfterMargin * (newImportPct / 100);
      const newTotalCost = newCostAfterMargin + newImportTax;

      onChange({
        baseCost,
        distributorMarginPct,
        importTaxPct: newImportPct,
        totalCost: newTotalCost
      });
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
        {label}
      </Typography>

      <Stack spacing={2}>
        {/* Base Cost Input */}
        <TextField
          label="Base Cost"
          type="number"
          value={baseCost || ''}
          onChange={(e) => handleBaseCostChange(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          inputProps={{
            min: 0,
            step: 0.01
          }}
          fullWidth
          size="small"
        />

        {/* Distributor Margin */}
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="body2" color="text.secondary">
              Distributor Margin
            </Typography>
            <TextField
              type="number"
              value={distributorMarginPct}
              onChange={(e) => handleMarginChange(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                step: 0.01
              }}
              size="small"
              sx={{ width: 100 }}
            />
          </Box>
          <Typography variant="body2" fontWeight="medium">
            ${distributorMargin.toFixed(2)}
          </Typography>
        </Box>

        <Divider />

        {/* Cost After Margin */}
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Cost + Margin
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            ${costAfterMargin.toFixed(2)}
          </Typography>
        </Box>

        {/* Import Tax */}
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="body2" color="text.secondary">
              Import Tax
            </Typography>
            <TextField
              type="number"
              value={importTaxPct}
              onChange={(e) => handleImportChange(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                step: 0.01
              }}
              size="small"
              sx={{ width: 100 }}
            />
          </Box>
          <Typography variant="body2" fontWeight="medium">
            ${importTax.toFixed(2)}
          </Typography>
        </Box>

        <Divider />

        {/* Total Cost */}
        <Box bgcolor="primary.50" p={1.5} borderRadius={1}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Total Cost (used for calculations)
          </Typography>
          <Typography variant="h6" color="primary.main" fontWeight="bold">
            ${totalCost.toFixed(2)}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
